const CONSENT_STORAGE_KEY = 'quizbrew_cookie_preferences';
const SETTINGS_STORAGE_KEY = 'pubquiz_settings';
const TELEMETRY_STORAGE_KEY = 'quizbrew_journey_events';
const CONSENT_VERSION = 1;

const defaultPreferences = {
    necessary: true,
    preferences: false,
    analytics: false,
    version: CONSENT_VERSION,
    decidedAt: null,
};

const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

const getLocalStorage = () => {
    if (!isBrowser) {
        return null;
    }

    try {
        return window.localStorage;
    } catch {
        return null;
    }
};

const getSessionStorage = () => {
    if (!isBrowser) {
        return null;
    }

    try {
        return window.sessionStorage;
    } catch {
        return null;
    }
};

const safeParse = (value) => {
    if (!value) {
        return null;
    }

    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
};

const readPreferences = () => {
    const storage = getLocalStorage();
    const stored = safeParse(storage?.getItem(CONSENT_STORAGE_KEY));
    if (!stored || typeof stored !== 'object') {
        return { ...defaultPreferences };
    }

    return {
        ...defaultPreferences,
        ...stored,
        necessary: true,
        version: CONSENT_VERSION,
    };
};

const hasDecision = () => Boolean(readPreferences().decidedAt);

const clearOptionalStorage = (preferences) => {
    const localStorageRef = getLocalStorage();
    const sessionStorageRef = getSessionStorage();

    if (!preferences.preferences) {
        localStorageRef?.removeItem(SETTINGS_STORAGE_KEY);
    }

    if (!preferences.analytics) {
        sessionStorageRef?.removeItem(TELEMETRY_STORAGE_KEY);
        if (isBrowser && Array.isArray(window.dataLayer)) {
            window.dataLayer.length = 0;
        }
    }
};

const emitConsentChange = (preferences) => {
    if (!isBrowser) {
        return;
    }

    window.dispatchEvent(
        new CustomEvent('quizbrew:cookie-consent-changed', {
            detail: preferences,
        })
    );
};

const writePreferences = (nextPreferences) => {
    const preferences = {
        ...defaultPreferences,
        ...nextPreferences,
        necessary: true,
        version: CONSENT_VERSION,
        decidedAt: new Date().toISOString(),
    };

    const storage = getLocalStorage();
    storage?.setItem(CONSENT_STORAGE_KEY, JSON.stringify(preferences));
    clearOptionalStorage(preferences);
    emitConsentChange(preferences);
    return preferences;
};

const canUse = (category) => {
    if (category === 'necessary') {
        return true;
    }

    return Boolean(readPreferences()[category]);
};

const shouldSuppressUi = () => {
    if (!isBrowser) {
        return true;
    }

    return window.location.hash.startsWith('#presenter/');
};

let bannerElement = null;
let modalElement = null;
let launcherElement = null;

const updateUiState = () => {
    if (!isBrowser || shouldSuppressUi()) {
        return;
    }

    const preferences = readPreferences();
    const decided = Boolean(preferences.decidedAt);

    if (bannerElement) {
        bannerElement.hidden = decided;
    }

    if (launcherElement) {
        launcherElement.hidden = !decided;
    }

    if (!modalElement) {
        return;
    }

    const preferenceToggle = modalElement.querySelector('[data-cookie-toggle="preferences"]');
    const analyticsToggle = modalElement.querySelector('[data-cookie-toggle="analytics"]');
    if (preferenceToggle) {
        preferenceToggle.checked = Boolean(preferences.preferences);
    }
    if (analyticsToggle) {
        analyticsToggle.checked = Boolean(preferences.analytics);
    }
};

const closePreferences = () => {
    if (!modalElement) {
        return;
    }

    modalElement.hidden = true;
    document.body.classList.remove('cookie-consent-modal-open');
};

const openPreferences = () => {
    if (!modalElement || shouldSuppressUi()) {
        return;
    }

    updateUiState();
    modalElement.hidden = false;
    document.body.classList.add('cookie-consent-modal-open');
};

const saveModalPreferences = () => {
    if (!modalElement) {
        return;
    }

    const preferenceToggle = modalElement.querySelector('[data-cookie-toggle="preferences"]');
    const analyticsToggle = modalElement.querySelector('[data-cookie-toggle="analytics"]');

    writePreferences({
        preferences: Boolean(preferenceToggle?.checked),
        analytics: Boolean(analyticsToggle?.checked),
    });

    updateUiState();
    closePreferences();
};

const buildBanner = () => {
    const banner = document.createElement('section');
    banner.className = 'cookie-consent-banner';
    banner.hidden = true;
    banner.innerHTML = `
        <div class="cookie-consent-card">
            <div class="cookie-consent-copy">
                <p class="cookie-consent-eyebrow">Cookie Settings</p>
                <h2>We use necessary cookies and storage to run QuizBrew.</h2>
                <p>Optional preferences and analytics stay off until you choose. You can change this any time.</p>
            </div>
            <div class="cookie-consent-actions">
                <button type="button" class="btn btn-ghost" data-cookie-action="customize">Choose Settings</button>
                <button type="button" class="btn btn-outline" data-cookie-action="necessary">Necessary Only</button>
                <button type="button" class="btn btn-primary" data-cookie-action="accept-all">Accept All</button>
            </div>
        </div>
    `;

    banner.querySelector('[data-cookie-action="accept-all"]')?.addEventListener('click', () => {
        writePreferences({ preferences: true, analytics: true });
        updateUiState();
    });

    banner.querySelector('[data-cookie-action="necessary"]')?.addEventListener('click', () => {
        writePreferences({ preferences: false, analytics: false });
        updateUiState();
    });

    banner.querySelector('[data-cookie-action="customize"]')?.addEventListener('click', openPreferences);

    return banner;
};

const buildModal = () => {
    const modal = document.createElement('div');
    modal.className = 'cookie-consent-modal';
    modal.hidden = true;
    modal.innerHTML = `
        <div class="cookie-consent-modal__backdrop" data-cookie-close></div>
        <div class="cookie-consent-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="cookieConsentTitle">
            <div class="cookie-consent-modal__header">
                <div>
                    <p class="cookie-consent-eyebrow">Privacy Controls</p>
                    <h2 id="cookieConsentTitle">Choose your cookie settings</h2>
                </div>
                <button type="button" class="cookie-consent-modal__close" aria-label="Close cookie settings" data-cookie-close>&times;</button>
            </div>
            <div class="cookie-consent-modal__body">
                <div class="cookie-consent-option cookie-consent-option--locked">
                    <div>
                        <h3>Necessary</h3>
                        <p>Required for login, security, billing, and core quiz operations.</p>
                    </div>
                    <span class="cookie-consent-lock">Always on</span>
                </div>
                <label class="cookie-consent-option">
                    <div>
                        <h3>Preferences</h3>
                        <p>Remembers venue defaults and layout settings on this device.</p>
                    </div>
                    <input type="checkbox" data-cookie-toggle="preferences">
                </label>
                <label class="cookie-consent-option">
                    <div>
                        <h3>Analytics</h3>
                        <p>Allows journey telemetry and future analytics tags to help improve the product.</p>
                    </div>
                    <input type="checkbox" data-cookie-toggle="analytics">
                </label>
                <p class="cookie-consent-footnote">
                    Read the full <a href="cookie-policy.html">Cookie Policy</a>.
                </p>
            </div>
            <div class="cookie-consent-modal__actions">
                <button type="button" class="btn btn-ghost" data-cookie-save="necessary">Necessary Only</button>
                <button type="button" class="btn btn-outline" data-cookie-save="selection">Save Settings</button>
                <button type="button" class="btn btn-primary" data-cookie-save="all">Accept All</button>
            </div>
        </div>
    `;

    modal.querySelectorAll('[data-cookie-close]').forEach((element) => {
        element.addEventListener('click', closePreferences);
    });

    modal.querySelector('[data-cookie-save="necessary"]')?.addEventListener('click', () => {
        writePreferences({ preferences: false, analytics: false });
        updateUiState();
        closePreferences();
    });

    modal.querySelector('[data-cookie-save="selection"]')?.addEventListener('click', saveModalPreferences);

    modal.querySelector('[data-cookie-save="all"]')?.addEventListener('click', () => {
        writePreferences({ preferences: true, analytics: true });
        updateUiState();
        closePreferences();
    });

    return modal;
};

const buildLauncher = () => {
    const launcher = document.createElement('button');
    launcher.type = 'button';
    launcher.className = 'cookie-consent-launcher';
    launcher.hidden = true;
    launcher.textContent = 'Cookie settings';
    launcher.addEventListener('click', openPreferences);
    return launcher;
};

const ensureUi = () => {
    if (!isBrowser || shouldSuppressUi() || bannerElement || modalElement || launcherElement) {
        return;
    }

    bannerElement = buildBanner();
    modalElement = buildModal();
    launcherElement = buildLauncher();

    document.body.appendChild(bannerElement);
    document.body.appendChild(modalElement);
    document.body.appendChild(launcherElement);

    document.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) {
            return;
        }

        const trigger = event.target.closest('[data-open-cookie-settings]');
        if (!trigger) {
            return;
        }

        event.preventDefault();
        openPreferences();
    });

    updateUiState();
};

const init = () => {
    clearOptionalStorage(readPreferences());

    if (!isBrowser) {
        return;
    }

    const syncUi = () => {
        if (shouldSuppressUi()) {
            if (bannerElement) {
                bannerElement.hidden = true;
            }
            if (modalElement) {
                modalElement.hidden = true;
            }
            if (launcherElement) {
                launcherElement.hidden = true;
            }
            document.body.classList.remove('cookie-consent-modal-open');
            return;
        }

        ensureUi();
        updateUiState();
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', syncUi, { once: true });
    } else {
        syncUi();
    }

    window.addEventListener('hashchange', syncUi);
};

export const cookieConsent = {
    readPreferences,
    hasDecision,
    canUse,
    save: writePreferences,
    openPreferences,
    closePreferences,
};

if (isBrowser) {
    window.quizbrewCookieConsent = cookieConsent;
    init();
}

export default cookieConsent;
